import {
  IconBuildingSkyscraper,
  IconCalendar,
  IconHeart,
  IconHeartFilled,
  IconPhotoFilled,
  IconPlane,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLocaleContext } from "src/context/locale-context";
import { favoritesActions, selectIsFavorited } from "src/store/favorites";
import { getAvioText } from "src/components/features/home/AvioCeljojumi/avioCeljojumiText";
import classes from "./ProductCards.module.css";

// Compact version of desktop's AvioHotelCard — same info blocks (package label, hotel+rating,
// price "no .../per person") plus the same favoriting (type "hotel" — this is the same
// cheapHotels data source desktop's AvioHotelCard uses), just resized for the mobile scroll card.
export function FlightHotelDealCard({ deal }) {
  const { getStringRes, currencySymbol } = useLocaleContext();
  const dispatch = useDispatch();
  const [imgError, setImgError] = useState(false);
  const hasImage = !imgError && !!deal.image;
  const rating = Number(deal.rating || 0);
  const hasRating = rating > 0;
  const favoriteId = `hotel-${deal.id}`;
  const isFavorite = useSelector((state) => selectIsFavorited(state, favoriteId));

  const onToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // `deal` here is HomePage.jsx's mapped shape (price already divided to per-person) — not
    // the raw cheapHotels item desktop's AvioHotelCard favorites (which has totalPrice+guests).
    // Storing `deal` as-is would make the /favorites page's CompactHotelCard (which expects
    // totalPrice/guests and divides them itself) show no price at all — same class of bug just
    // fixed for HotelResultItem. `guests: 1` is a no-op divisor since deal.price is already
    // per-person.
    dispatch(
      favoritesActions.toggleFavorite({
        id: favoriteId,
        type: "hotel",
        data: { ...deal, totalPrice: deal.price, guests: 1 },
      })
    );
    if (!isFavorite) {
      toast.success(getStringRes("favorites.toast.saved"));
    }
  };

  const body = (
    <>
      <div className={classes.fhImageWrap}>
        {hasImage ? (
          <img
            src={deal.image}
            alt={deal.city}
            className={classes.fhImage}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={classes.vacationImageFallback}>
            <IconPhotoFilled size="35%" color="var(--ui-4)" />
          </div>
        )}
        <span className={classes.fhBadge}>
          <IconPlane size={13} stroke={1.75} />
          <span aria-hidden="true">+</span>
          <IconBuildingSkyscraper size={13} stroke={1.75} />
        </span>
      </div>
      <div className={classes.fhBody}>
        <h3 className={classes.fhName}>{deal.city}</h3>
        <p className={classes.fhPackageLabel}>
          <IconPlane size={13} stroke={1.75} />
          <IconBuildingSkyscraper size={13} stroke={1.75} />
          {getAvioText(getStringRes, "avio.deals.badge")}
        </p>

        {deal.dates && (
          <div className={classes.fhMetaRow}>
            <IconCalendar size={14} stroke={1.75} />
            <span>{deal.dates}</span>
          </div>
        )}

        {hasRating ? (
          // `rating` is a 0–10 guest review score, not a 1–5 star class — Math.round(rating)
          // stars used to overflow to "all 5 filled" for any score ≥5.
          <div className={classes.fhStars}>
            <span className={`${classes.fhRatingChip} ${rating >= 8 ? classes.fhRatingGood : ""}`}>
              {rating.toFixed(1)}
            </span>
          </div>
        ) : (
          <span className={classes.fhRatingNone}>
            {getAvioText(getStringRes, "cheap.hotels.rating.none")}
          </span>
        )}

        {deal.name && <p className={classes.fhHotelName}>{deal.name}</p>}

        <hr className={classes.fhDivider} />

        <div className={classes.fhPriceRow}>
          {Number.isFinite(deal.price) ? (
            <>
              <span className={classes.priceLabel}>
                {getAvioText(getStringRes, "avio.deals.priceFrom")}
              </span>
              <div className={classes.fhPriceLine}>
                <span className={classes.priceAmountSm}>
                  {deal.price.toFixed(0)} {currencySymbol}
                </span>
                <span className={classes.fhPricePerPerson}>
                  {getStringRes("cheap.hotels.price.per.person")}
                </span>
              </div>
            </>
          ) : (
            <span className={classes.priceNone}>{getStringRes("cheap.hotels.price.none")}</span>
          )}
        </div>
      </div>
    </>
  );

  const isExternal = deal.link?.startsWith("http");

  const cardLink = !deal.link ? (
    <div className={classes.fhCard}>{body}</div>
  ) : isExternal ? (
    <a href={deal.link} target="_blank" rel="noopener noreferrer" className={classes.fhCard}>
      {body}
    </a>
  ) : (
    <Link to={deal.link} className={classes.fhCard}>
      {body}
    </Link>
  );

  return (
    <div className={classes.fhFavoriteWrap}>
      <button
        type="button"
        className={classes.fhFavoriteButton}
        aria-label={getStringRes("cheap.hotels.favorite")}
        onClick={onToggleFavorite}>
        {isFavorite ? (
          <IconHeartFilled size={14} color="#ff2965" />
        ) : (
          <IconHeart size={14} stroke={1.75} />
        )}
      </button>
      {cardLink}
    </div>
  );
}
